import {ACTION_BAR_HEIGHT, Typography} from 'nf-ui'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import {PageLoading} from '~/components/PageLoading'
import {getGraphQLErrorMessage} from '~/util'
import {FullOrgChartProvider, useFullOrgChart} from '../FullOrgChart/FullOrgChartContext'
import {DisplayOptionsProvider} from '../FullOrgChart/useDisplayOptions'
import {OpenPositionProvider} from '../FullOrgChart/OpenPositions/useOpenPosition'
import {useOrgChartData} from '../FullOrgChart/useOrgChartData'
import {Profile} from '../useFullOrgChartData'
import {HorizontalTree} from './HorizontalTree'

const OuterContainer = styled.div`
    position: absolute;
    height: 100vh;
    width: 100%;
`

const ScrollContainer = styled.div`
    position: relative;
    padding: 32px;
    padding-bottom: calc(64px + ${ACTION_BAR_HEIGHT}px);
    overflow-y: auto;
    overflow-x: auto;
    height: 100vh;
    box-sizing: border-box;
`

type TreeNode = {
    profile: Profile
    x: number
    y: number
    children: TreeNode[]
    // Hide only card content while keeping subtree container mounted (used for visible-path lock).
    isContentHidden?: boolean
    // When true, this node (and any connectors to its children) are not rendered.
    // Crucially, we still compute x/y for hidden nodes so collapsing does not relocate siblings.
    isHidden?: boolean
    hasStructuralChildren: boolean
    parent?: TreeNode
    isAdditional?: boolean
    isLeafGroup?: boolean // True if this node is part of a group of leaf nodes stacked vertically
    // New properties for sibling group system
    relativeX?: number // Relative X within sibling group
    relativeY?: number // Relative Y within sibling group
    siblingGroup?: SiblingGroup // Reference to the sibling group this node belongs to
}

type SiblingGroup = {
    parent: TreeNode | null // null for root level groups
    isLeaf: boolean // true if all items in group have no children
    rowIndex: number // recursion depth
    contentColumns: number // number of non-leaf items OR number of leaf items (capped at 5)
    contentRows: number // 1 if non-leaf, otherwise calculated based on leaf count
    subTreeRows: number // max rows needed for deepest part of subtrees
    items: TreeNode[] // items in this group
    relativeX: number // relative X coordinate within parent's layout
    relativeY: number // relative Y coordinate (always 0 for a group)
    absoluteX: number // absolute X coordinate (center of group)
    absoluteY: number // absolute Y coordinate (row index)
    width: number // width of this group
}

const HorizontalOrgChartContent = ({
    treeNodes,
    getProfileFromIdStr,
    isExpanded,
    toggleExpand,
    collapsingProfileIds,
    collapsedProfileIds,
}: {
    treeNodes: TreeNode[]
    getProfileFromIdStr: (idStr: string) => Profile | null
    isExpanded: (profileIdStr: string) => boolean
    toggleExpand: (profileIdStr: string) => void
    collapsingProfileIds: Set<string>
    collapsedProfileIds: Set<string>
}) => {
    const {scrollContainer} = useFullOrgChart()
    const hasCenteredInitialScrollRef = useRef(false)

    useEffect(() => {
        const scrollEl = scrollContainer.current
        if (!scrollEl || hasCenteredInitialScrollRef.current || treeNodes.length === 0) return

        const childIdSet = new Set<string>()
        for (const node of treeNodes) {
            for (const child of node.children) childIdSet.add(child.profile.idStr)
        }
        const rootNodes = treeNodes.filter((node) => !childIdSet.has(node.profile.idStr))
        if (rootNodes.length === 0) return

        const treeMinX = Math.min(...treeNodes.map((n) => n.x))
        const treeMaxX = Math.max(...treeNodes.map((n) => n.x + 120))
        const treeCenterX = (treeMinX + treeMaxX) / 2
        const firstRoot = [...rootNodes].sort((a, b) => {
            const aCenter = a.x + 120 / 2
            const bCenter = b.x + 120 / 2
            return Math.abs(aCenter - treeCenterX) - Math.abs(bCenter - treeCenterX)
        })[0]
        const profileWidth = 120
        const rootCenterX = firstRoot.x + profileWidth / 2
        const targetScrollLeft = Math.max(0, rootCenterX - scrollEl.clientWidth / 2)
        const maxScrollLeft = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth)
        scrollEl.scrollLeft = Math.min(targetScrollLeft, maxScrollLeft)
        hasCenteredInitialScrollRef.current = true
    }, [treeNodes, scrollContainer])

    return (
        <ScrollContainer ref={scrollContainer}>
            <HorizontalTree
                treeNodes={treeNodes}
                getProfileFromIdStr={getProfileFromIdStr}
                isExpanded={isExpanded}
                toggleExpand={toggleExpand}
                collapsingProfileIds={collapsingProfileIds}
                collapsedProfileIds={collapsedProfileIds}
            />
        </ScrollContainer>
    )
}

export const HorizontalOrgChart = () => {
    const {data, loading, error, topLevelProfiles, visibleProfiles, getProfileFromIdStr} = useOrgChartData()
    const [collapsedProfiles, setCollapsedProfiles] = useState<Set<string>>(new Set())
    const [collapsingProfiles, setCollapsingProfiles] = useState<Set<string>>(new Set())
    const collapseTimersRef = useRef<Map<string, number>>(new Map())
    const orgChartContainer = useRef<HTMLDivElement>(null)
    // Snapshot of fully-expanded horizontal sibling ordering.
    // Key: parentIdStr (or virtual root key), Value: childIdStr -> order index (left-to-right).
    const baselineChildOrderIndexByParentRef = useRef<Map<string, Map<string, number>> | null>(null)
    // Freeze layout after first fully-expanded render:
    // when collapsing, we reuse cached node positions + children structure and only update `isHidden`.
    const fullyExpandedLayoutSnapshotRef = useRef<{
        signature: string
        nodesById: Map<string, {x: number; y: number; hasStructuralChildren: boolean}>
        childrenById: Map<string, string[]>
        rootsById: string[]
    } | null>(null)

    // Build tree structure with positions
    // NOTE: This hook must be called before any conditional returns to follow Rules of Hooks
    const treeNodes = useMemo(() => {
        if (!topLevelProfiles.length) return []

        const visibleSignature = [...visibleProfiles]
            .map((p) => p.idStr)
            .sort()
            .join('|')

        // If we have a frozen snapshot, always render from it.
        // `collapsedProfiles` and `visibleProfiles` only control `isHidden`, never coordinates.
        if (fullyExpandedLayoutSnapshotRef.current) {
            const snapshot = fullyExpandedLayoutSnapshotRef.current
            if (snapshot) {
                const visibleIdSet = new Set(visibleProfiles.map((p) => p.idStr))
                const isBranchScopedView = visibleIdSet.size < snapshot.nodesById.size
                const nodeMap = new Map<string, TreeNode>()
                // Create node objects first.
                for (const [idStr, meta] of snapshot.nodesById.entries()) {
                    const profile = getProfileFromIdStr(idStr)
                    if (!profile) continue
                    nodeMap.set(idStr, {
                        profile,
                        x: meta.x,
                        y: meta.y,
                        children: [],
                        isContentHidden: false,
                        isHidden: false,
                        hasStructuralChildren: meta.hasStructuralChildren,
                    })
                }

                // Wire children pointers.
                for (const [parentIdStr, childIds] of snapshot.childrenById.entries()) {
                    const parent = nodeMap.get(parentIdStr)
                    if (!parent) continue
                    parent.children = childIds.map((cid) => nodeMap.get(cid)).filter((c): c is TreeNode => Boolean(c))
                    for (const ch of parent.children) ch.parent = parent
                }

                // Visible path lock:
                // - Keep containers mounted for ancestors of visible nodes.
                // - Hide only their card content via `isContentHidden`.
                const inVisiblePath = new Set<string>()
                const markVisiblePath = (node: TreeNode): boolean => {
                    const selfVisible = visibleIdSet.has(node.profile.idStr)
                    const childVisible = node.children.some((ch) => markVisiblePath(ch))
                    const onPath = selfVisible || childVisible
                    if (onPath) inVisiblePath.add(node.profile.idStr)
                    return onPath
                }
                const roots = snapshot.rootsById
                    .map((idStr) => nodeMap.get(idStr))
                    .filter((n): n is TreeNode => Boolean(n))
                for (const r of roots) markVisiblePath(r)

                // In branch-scoped mode, render the full subtree of the branch root(s),
                // while keeping ancestor containers mounted (content hidden).
                const inBranchSubtree = new Set<string>()
                const inAncestorPath = new Set<string>()
                if (isBranchScopedView) {
                    const branchRoots = Array.from(nodeMap.values()).filter((n) => {
                        if (!visibleIdSet.has(n.profile.idStr)) return false
                        return !n.parent || !visibleIdSet.has(n.parent.profile.idStr)
                    })

                    const markSubtree = (n: TreeNode) => {
                        inBranchSubtree.add(n.profile.idStr)
                        for (const ch of n.children) markSubtree(ch)
                    }
                    const markAncestors = (n: TreeNode) => {
                        let cur = n.parent
                        while (cur) {
                            inAncestorPath.add(cur.profile.idStr)
                            cur = cur.parent
                        }
                    }

                    for (const br of branchRoots) {
                        markSubtree(br)
                        markAncestors(br)
                    }
                }

                const dfsApplyVisibility = (node: TreeNode, ancestorCollapsed: boolean) => {
                    const selfCollapsed = collapsedProfiles.has(node.profile.idStr)
                    if (!isBranchScopedView) {
                        // Normal mode: collapse only hides descendants. No path locking.
                        node.isHidden = ancestorCollapsed
                        node.isContentHidden = ancestorCollapsed
                    } else {
                        const onVisiblePath =
                            inBranchSubtree.has(node.profile.idStr) || inAncestorPath.has(node.profile.idStr)
                        // Unrelated branches are fully hidden. Path nodes remain mounted.
                        node.isHidden = !onVisiblePath || ancestorCollapsed
                        // Path-only ancestors keep container but hide content.
                        node.isContentHidden =
                            onVisiblePath && (ancestorCollapsed || !inBranchSubtree.has(node.profile.idStr))
                    }

                    for (const ch of node.children) {
                        dfsApplyVisibility(ch, ancestorCollapsed || selfCollapsed)
                    }
                }

                for (const r of roots) dfsApplyVisibility(r, false)

                // Return all nodes (including hidden) so origin normalization stays stable.
                return Array.from(nodeMap.values())
            }
        }

        type LayoutResult = {
            nodes: TreeNode[]
            width: number
            height: number
        }

        const buildLayoutForLeafCols = (leafMaxCols: number, captureSnapshots: boolean): LayoutResult => {
            const nodeMap = new Map<string, TreeNode>()
            const visibleIdSet = new Set(visibleProfiles.map((p) => p.idStr))

            // First pass: create all nodes
            const createNode = (profile: Profile, parent?: TreeNode, isAdditional = false): TreeNode => {
                if (nodeMap.has(profile.idStr)) {
                    return nodeMap.get(profile.idStr)!
                }

                const node: TreeNode = {
                    profile,
                    x: 0,
                    y: parent ? parent.y + 1 : 0,
                    children: [],
                    isContentHidden: false,
                    isHidden: false,
                    hasStructuralChildren:
                        profile.directReports
                            .map((idStr) => getProfileFromIdStr(idStr))
                            .filter((p): p is Profile => p !== null && visibleIdSet.has(p.idStr)).length +
                            profile.additionalReports
                                .map((idStr) => getProfileFromIdStr(idStr))
                                .filter((p): p is Profile => p !== null && visibleIdSet.has(p.idStr)).length >
                        0,
                    parent,
                    isAdditional,
                }
                nodeMap.set(profile.idStr, node)
                return node
            }

            // Build tree structure recursively.
            // Important: a structural node is attached to at most ONE parent in the layout tree.
            // If the same profile appears under multiple parents (e.g. via additionalReports),
            // we only keep the first structural attachment and skip subsequent ones.
            const buildTree = (profiles: Profile[], parent?: TreeNode, isAdditional = false) => {
                for (const profile of profiles) {
                    // Only include profiles that are part of the current visible set
                    if (!visibleIdSet.has(profile.idStr)) continue

                    const node = createNode(profile, parent, isAdditional)
                    // Only attach if this node actually belongs to this parent in the primary tree.
                    if (parent && node.parent === parent) {
                        parent.children.push(node)
                    }

                    // Always build the full structural subtree so collapse can be handled by visibility only.
                    const directReports = profile.directReports
                        .map((idStr) => getProfileFromIdStr(idStr))
                        .filter((p): p is Profile => p !== null && visibleIdSet.has(p.idStr))
                    const additionalReports = profile.additionalReports
                        .map((idStr) => getProfileFromIdStr(idStr))
                        .filter((p): p is Profile => p !== null && visibleIdSet.has(p.idStr))

                    buildTree(directReports, node, false)
                    buildTree(additionalReports, node, true)
                }
            }

            buildTree(topLevelProfiles)

            // Collapse should only hide descendants, not relocate any siblings.
            // Mark nodes hidden if any ancestor is collapsed.
            const rootsForHidden = topLevelProfiles
                .filter((p) => visibleIdSet.has(p.idStr))
                .map((p) => nodeMap.get(p.idStr)!)
                .filter(Boolean)

            const markHiddenByCollapsedAncestors = (n: TreeNode, ancestorCollapsed: boolean) => {
                n.isHidden = ancestorCollapsed
                n.isContentHidden = ancestorCollapsed
                const selfCollapsed = collapsedProfiles.has(n.profile.idStr)
                for (const ch of n.children) {
                    markHiddenByCollapsedAncestors(ch, ancestorCollapsed || selfCollapsed)
                }
            }

            for (const r of rootsForHidden) {
                markHiddenByCollapsedAncestors(r, false)
            }

            // NEW LAYOUT SYSTEM: Subtree dimension balancing
            const PROFILE_WIDTH = 120
            const PROFILE_HEIGHT = 140
            const DEFAULT_ROW_GAP = 64 // Must match HorizontalTree.tsx ROW_VERTICAL_GAP
            const HORIZONTAL_GAP = 30
            const LEAF_MAX_COLS = leafMaxCols
            const LEAF_ROW_GAP = HORIZONTAL_GAP
            const DEFAULT_ROW_HEIGHT_PX = PROFILE_HEIGHT + DEFAULT_ROW_GAP
            const LEAF_ROW_HEIGHT_PX = PROFILE_HEIGHT + LEAF_ROW_GAP
            const LEAF_ROW_STEP_UNITS = LEAF_ROW_HEIGHT_PX / DEFAULT_ROW_HEIGHT_PX

            // Height of profile cards is handled by `HorizontalTree` via `ROW_HEIGHT`.
            // Here, `y` is a row index (0-based), and "height" means number of rows.
            const calculateContentRows = (leafCount: number): number => {
                if (leafCount <= 0) return 0
                // Kept from the previous implementation to match the desired visual rhythm.
                return 1 + Math.ceil(0.66 * Math.ceil((leafCount - 1) / LEAF_MAX_COLS))
            }

            type SubtreeDims = {
                width: number // width occupied by the subtree's children (px). For leaf nodes: card width.
                heightRows: number // number of occupied rows including the node's own card row
            }

            const subtreeDimsById = new Map<string, SubtreeDims>()

            const getPromotedSingleLeaf = (node: TreeNode): TreeNode | null => {
                const leafChildren = node.children.filter((c) => !c.hasStructuralChildren)
                return leafChildren.length === 1 ? leafChildren[0] : null
            }

            // Rule: if a parent has exactly one leaf child, treat it as non-leaf for layout grouping.
            const getNonLeafChildren = (node: TreeNode) => {
                const promotedLeaf = getPromotedSingleLeaf(node)
                return node.children.filter((c) => c.hasStructuralChildren || c === promotedLeaf)
            }

            const getLeafChildren = (node: TreeNode) => {
                const promotedLeaf = getPromotedSingleLeaf(node)
                return node.children.filter((c) => !c.hasStructuralChildren && c !== promotedLeaf)
            }

            const getLeafSpecialDims = (leafCount: number): {width: number; heightRows: number; cols: number} => {
                const cols = Math.min(LEAF_MAX_COLS, leafCount)
                const contentRows = calculateContentRows(leafCount)
                // Rows within the same leaf group use reduced vertical spacing.
                // First row takes 1 unit; each additional row advances by LEAF_ROW_STEP_UNITS.
                const heightRows = contentRows <= 0 ? 0 : 1 + Math.max(0, contentRows - 1) * LEAF_ROW_STEP_UNITS
                const width = cols * PROFILE_WIDTH + (cols - 1) * HORIZONTAL_GAP
                return {width, heightRows, cols}
            }

            const computeSubtreeDims = (node: TreeNode): SubtreeDims => {
                const existing = subtreeDimsById.get(node.profile.idStr)
                if (existing) return existing

                // Leaf card: 1 row, and it occupies one block width.
                if (node.children.length === 0) {
                    const dims: SubtreeDims = {width: PROFILE_WIDTH, heightRows: 1}
                    subtreeDimsById.set(node.profile.idStr, dims)
                    return dims
                }

                const nonLeafChildren = getNonLeafChildren(node)
                const leafChildren = getLeafChildren(node)

                const nonLeafCount = nonLeafChildren.length

                // Sum child subtree widths (plus gaps between them).
                let childrenWidth = 0
                let maxChildHeight = 0
                for (const child of nonLeafChildren) {
                    const d = computeSubtreeDims(child)
                    childrenWidth += d.width
                    maxChildHeight = Math.max(maxChildHeight, d.heightRows)
                }
                if (nonLeafCount > 0) {
                    childrenWidth += (nonLeafCount - 1) * HORIZONTAL_GAP
                }

                // Optional special node for all leaf children.
                let leafSpecialWidth = 0
                let leafSpecialHeightRows = 0
                if (leafChildren.length > 0) {
                    const leafSpecial = getLeafSpecialDims(leafChildren.length)
                    leafSpecialWidth = leafSpecial.width
                    leafSpecialHeightRows = leafSpecial.heightRows
                    // Add +1 horizontal gap between the last non-leaf child and the special node.
                    if (nonLeafCount > 0) {
                        childrenWidth += HORIZONTAL_GAP + leafSpecialWidth
                    } else {
                        childrenWidth = leafSpecialWidth
                    }
                }

                const heightRows = 1 + Math.max(maxChildHeight, leafSpecialHeightRows)

                const dims: SubtreeDims = {width: childrenWidth, heightRows}
                subtreeDimsById.set(node.profile.idStr, dims)
                return dims
            }

            // Entities are either a non-leaf child node, or the "special" grouped leaf node.
            const VIRTUAL_ROOT_KEY = '__virtual_root__'

            type PlacementEntity =
                | {
                      kind: 'node'
                      node: TreeNode
                      width: number // visible subtree width used for placement and collision envelopes
                      heightRows: number // visible subtree height in rows
                      layoutWidth: number // for placement at this level (immediate block width)
                  }
                | {
                      kind: 'special'
                      leafNodes: TreeNode[]
                      width: number // visible width used for placement and collision envelopes
                      heightRows: number // visible height in rows
                      cols: number
                      layoutWidth: number // for placement at this level
                  }

            const placeLeafSpecial = (leafNodes: TreeNode[], centerX: number, topRowIndex: number, cols: number) => {
                const {width} = getLeafSpecialDims(leafNodes.length)
                const left = centerX - width / 2

                for (let i = 0; i < leafNodes.length; i++) {
                    const row = Math.floor(i / LEAF_MAX_COLS)
                    const col = i % LEAF_MAX_COLS
                    leafNodes[i].x = left + col * (PROFILE_WIDTH + HORIZONTAL_GAP)
                    leafNodes[i].y = topRowIndex + row * LEAF_ROW_STEP_UNITS
                }
            }

            const collectSubtreeNodes = (root: TreeNode): TreeNode[] => {
                const acc: TreeNode[] = []
                const dfs = (n: TreeNode) => {
                    acc.push(n)
                    for (const ch of n.children) dfs(ch)
                }
                dfs(root)
                return acc
            }

            const shiftNodes = (nodes: TreeNode[], deltaX: number) => {
                if (Math.abs(deltaX) < 1e-6) return
                for (const n of nodes) n.x += deltaX
            }

            // Overlap in "rendering space" (pixels), matching `HorizontalTree`:
            // - topPx = round(y * ROW_HEIGHT)
            // - item height ≈ PROFILE_HEIGHT
            const toPxY = (rowY: number) => Math.round(rowY * DEFAULT_ROW_HEIGHT_PX)
            const nodeVerticalTopPx = (n: TreeNode) => toPxY(n.y)
            const nodeVerticalBottomPx = (n: TreeNode) => nodeVerticalTopPx(n) + PROFILE_HEIGHT

            // Returns how far `movingNodes` can shift left before it would overlap `fixedNodes`.
            const maxSafeLeftShift = (movingNodes: TreeNode[], fixedNodes: TreeNode[]): number => {
                let maxLeftShift = Infinity
                let hasVerticalOverlap = false

                for (const moving of movingNodes) {
                    const movingTopPx = nodeVerticalTopPx(moving)
                    const movingBottomPx = nodeVerticalBottomPx(moving)
                    const movingLeft = moving.x

                    for (const fixed of fixedNodes) {
                        const fixedTopPx = nodeVerticalTopPx(fixed)
                        const fixedBottomPx = nodeVerticalBottomPx(fixed)
                        const overlapsVertically =
                            Math.max(movingTopPx, fixedTopPx) < Math.min(movingBottomPx, fixedBottomPx)
                        if (!overlapsVertically) continue

                        hasVerticalOverlap = true
                        const allowedShift = movingLeft - (fixed.x + PROFILE_WIDTH + HORIZONTAL_GAP)
                        maxLeftShift = Math.min(maxLeftShift, allowedShift)
                    }
                }

                if (!hasVerticalOverlap || !isFinite(maxLeftShift)) return 0
                return Math.max(0, maxLeftShift)
            }

            const placeEntities = (
                entities: PlacementEntity[],
                totalWidth: number,
                parentCenterX: number,
                topRowIndex: number,
                parentOrderKey: string,
            ) => {
                if (entities.length === 0) return
                const nodesToShift = new Set<TreeNode>()
                const placedEntities: PlacementEntity[] = []
                const buildEntityNodeList = (entity: PlacementEntity): TreeNode[] =>
                    entity.kind === 'node' ? collectSubtreeNodes(entity.node) : entity.leafNodes

                const pageCenterX = 0 // by construction we place the virtual root centered at X=0
                const baselineOrderForParent = baselineChildOrderIndexByParentRef.current?.get(parentOrderKey)
                const getEntityOrderIndex = (e: PlacementEntity): number => {
                    if (!baselineOrderForParent) return Infinity
                    if (e.kind === 'node') {
                        return baselineOrderForParent.get(e.node.profile.idStr) ?? Infinity
                    }
                    let minIdx = Infinity
                    for (const n of e.leafNodes) {
                        const idx = baselineOrderForParent.get(n.profile.idStr) ?? Infinity
                        minIdx = Math.min(minIdx, idx)
                    }
                    return minIdx
                }

                const sorted = [...entities].sort((a, b) => {
                    // If we have a baseline snapshot for this sibling set, preserve its left-to-right order.
                    const aIdx = getEntityOrderIndex(a)
                    const bIdx = getEntityOrderIndex(b)
                    if (aIdx !== bIdx) return aIdx - bIdx

                    // Otherwise fallback to width/height ordering.
                    if (a.width !== b.width) return b.width - a.width
                    return a.heightRows - b.heightRows
                })

                let leftCursor = parentCenterX - totalWidth / 2
                let rightCursor = parentCenterX + totalWidth / 2
                let remaining = sorted.length
                // Stable side assignment:
                // choose which side is farther from global center for this parent, then alternate.
                const farSideIsLeft = parentCenterX <= pageCenterX

                for (let index = 0; index < sorted.length; index++) {
                    const entity = sorted[index]
                    // Cursor math uses subtree width to avoid overlap between sibling subtrees.
                    const leftCenterX = leftCursor + entity.width / 2
                    const rightCenterX = rightCursor - entity.width / 2

                    // Do not let collapse/expand alter side ordering by re-evaluating distances each step.
                    // Even indices go to the far side, odd indices go to the near side.
                    const placeOnLeft = index % 2 === 0 ? farSideIsLeft : !farSideIsLeft

                    const entityCenterX = placeOnLeft ? leftCenterX : rightCenterX

                    if (entity.kind === 'node') {
                        placeNode(entity.node, entityCenterX, topRowIndex)
                        for (const n of collectSubtreeNodes(entity.node)) nodesToShift.add(n)
                    } else {
                        placeLeafSpecial(entity.leafNodes, entityCenterX, topRowIndex, entity.cols)
                        for (const n of entity.leafNodes) nodesToShift.add(n)
                    }
                    placedEntities.push(entity)

                    // Consume space. When only one entity remains, don't consume an extra gap.
                    if (placeOnLeft) {
                        leftCursor += entity.width
                        if (remaining > 1) leftCursor += HORIZONTAL_GAP
                    } else {
                        rightCursor -= entity.width
                        if (remaining > 1) rightCursor -= HORIZONTAL_GAP
                    }

                    remaining -= 1
                }

                // Compaction pass:
                // move each subtree left as much as possible without overlapping previously placed
                // sibling subtrees at any vertically overlapping band.
                const baselineOrderForParentDuringCompaction = baselineChildOrderIndexByParentRef.current?.get(
                    parentOrderKey,
                )
                const getEntityOrderIndexForCompaction = (e: PlacementEntity): number => {
                    if (!baselineOrderForParentDuringCompaction) return Infinity
                    if (e.kind === 'node')
                        return baselineOrderForParentDuringCompaction.get(e.node.profile.idStr) ?? Infinity
                    let minIdx = Infinity
                    for (const n of e.leafNodes) {
                        minIdx = Math.min(
                            minIdx,
                            baselineOrderForParentDuringCompaction.get(n.profile.idStr) ?? Infinity,
                        )
                    }
                    return minIdx
                }

                const entitiesByX = [...placedEntities].sort((a, b) => {
                    // Prefer baseline sibling ordering for monotonic compaction.
                    const aOrder = getEntityOrderIndexForCompaction(a)
                    const bOrder = getEntityOrderIndexForCompaction(b)
                    if (aOrder !== bOrder) return aOrder - bOrder

                    const aX =
                        a.kind === 'node'
                            ? a.node.x + PROFILE_WIDTH / 2
                            : a.leafNodes.reduce((s, n) => s + n.x + PROFILE_WIDTH / 2, 0) / a.leafNodes.length
                    const bX =
                        b.kind === 'node'
                            ? b.node.x + PROFILE_WIDTH / 2
                            : b.leafNodes.reduce((s, n) => s + n.x + PROFILE_WIDTH / 2, 0) / b.leafNodes.length
                    return aX - bX
                })

                if (entitiesByX.length > 1) {
                    const accumulatedNodes: TreeNode[] = [...buildEntityNodeList(entitiesByX[0])]
                    let prevEntityNodes = [...buildEntityNodeList(entitiesByX[0])]
                    for (let i = 1; i < entitiesByX.length; i++) {
                        const entity = entitiesByX[i]
                        const entityNodes = buildEntityNodeList(entity)
                        let leftShift = maxSafeLeftShift(entityNodes, accumulatedNodes)

                        // Monotonic constraint to prevent crossing baseline siblings:
                        // ensure entity's left edge never moves past the previous entity's right edge + gap.
                        if (baselineOrderForParentDuringCompaction) {
                            const prevRight = Math.max(...prevEntityNodes.map((n) => n.x + PROFILE_WIDTH))
                            const entityLeft = Math.min(...entityNodes.map((n) => n.x))
                            const maxShiftByOrder = entityLeft - (prevRight + HORIZONTAL_GAP)
                            leftShift = Math.min(leftShift, maxShiftByOrder)
                        }

                        if (leftShift > 1e-6) {
                            shiftNodes(entityNodes, -leftShift)
                            prevEntityNodes = entityNodes
                        } else {
                            prevEntityNodes = entityNodes
                        }
                        accumulatedNodes.push(...entityNodes)
                    }
                }

                // Recompute spans after compaction.
                let placedLevelLeft = Infinity
                let placedLevelRight = -Infinity
                let subtreePlacedLeft = Infinity
                let subtreePlacedRight = -Infinity

                for (const entity of placedEntities) {
                    if (entity.kind === 'node') {
                        const immediateLeft = entity.node.x
                        const immediateRight = entity.node.x + PROFILE_WIDTH
                        placedLevelLeft = Math.min(placedLevelLeft, immediateLeft)
                        placedLevelRight = Math.max(placedLevelRight, immediateRight)
                    } else {
                        const immediateLeft = Math.min(...entity.leafNodes.map((n) => n.x))
                        const immediateRight = Math.max(...entity.leafNodes.map((n) => n.x + PROFILE_WIDTH))
                        placedLevelLeft = Math.min(placedLevelLeft, immediateLeft)
                        placedLevelRight = Math.max(placedLevelRight, immediateRight)
                    }

                    const nodes = buildEntityNodeList(entity)
                    const subLeft = Math.min(...nodes.map((n) => n.x))
                    const subRight = Math.max(...nodes.map((n) => n.x + PROFILE_WIDTH))
                    subtreePlacedLeft = Math.min(subtreePlacedLeft, subLeft)
                    subtreePlacedRight = Math.max(subtreePlacedRight, subRight)
                }

                // Enforce centering: shift the entire child subtree so the placed child blocks
                // are centered exactly under `parentCenterX`.
                if (nodesToShift.size > 0 && placedLevelLeft !== Infinity && placedLevelRight !== -Infinity) {
                    const currentCenterX = (placedLevelLeft + placedLevelRight) / 2
                    const delta = parentCenterX - currentCenterX
                    if (Math.abs(delta) > 1e-6) {
                        // Clamp delta so we don't shift the children outside their allocated subtree bounds.
                        if (subtreePlacedLeft !== Infinity && subtreePlacedRight !== -Infinity) {
                            const allocatedLeft = parentCenterX - totalWidth / 2
                            const allocatedRight = parentCenterX + totalWidth / 2
                            const minDelta = allocatedLeft - subtreePlacedLeft
                            const maxDelta = allocatedRight - subtreePlacedRight
                            const clampedDelta = Math.min(Math.max(delta, minDelta), maxDelta)
                            for (const n of nodesToShift) n.x += clampedDelta
                        } else {
                            for (const n of nodesToShift) n.x += delta
                        }
                    }
                }
            }

            const placeNode = (node: TreeNode, centerX: number, topRowIndex: number) => {
                node.x = centerX - PROFILE_WIDTH / 2
                node.y = topRowIndex

                if (node.children.length === 0) return

                const nonLeafChildren = getNonLeafChildren(node)
                const leafChildren = getLeafChildren(node)

                const baselineOrderForThisParent = baselineChildOrderIndexByParentRef.current?.get(node.profile.idStr)
                if (baselineOrderForThisParent) {
                    // Keep leaf and non-leaf internal ordering stable relative to the baseline snapshot.
                    nonLeafChildren.sort(
                        (a, b) =>
                            (baselineOrderForThisParent.get(a.profile.idStr) ?? Infinity) -
                            (baselineOrderForThisParent.get(b.profile.idStr) ?? Infinity),
                    )
                    leafChildren.sort(
                        (a, b) =>
                            (baselineOrderForThisParent.get(a.profile.idStr) ?? Infinity) -
                            (baselineOrderForThisParent.get(b.profile.idStr) ?? Infinity),
                    )
                }

                const dims = subtreeDimsById.get(node.profile.idStr)
                if (!dims) return

                const nonLeafEntities: PlacementEntity[] = []
                for (const child of nonLeafChildren) {
                    const cd = subtreeDimsById.get(child.profile.idStr)
                    if (!cd) continue
                    nonLeafEntities.push({
                        kind: 'node',
                        node: child,
                        width: cd.width,
                        heightRows: cd.heightRows,
                        layoutWidth: PROFILE_WIDTH,
                    })
                }

                let leafEntity: PlacementEntity | null = null
                if (leafChildren.length > 0) {
                    const leafSpecial = getLeafSpecialDims(leafChildren.length)
                    leafEntity = {
                        kind: 'special',
                        leafNodes: leafChildren,
                        width: leafSpecial.width,
                        heightRows: leafSpecial.heightRows,
                        cols: leafSpecial.cols,
                        layoutWidth: leafSpecial.width,
                    }
                }

                const hasMixedGroups = nonLeafEntities.length > 0 && leafEntity !== null

                if (hasMixedGroups) {
                    // Keep non-leaf and leaf groups separate (never inter-mingle in ordering).
                    const nonLeafGroupWidth =
                        nonLeafEntities.reduce((sum, e) => sum + e.width, 0) +
                        Math.max(0, nonLeafEntities.length - 1) * HORIZONTAL_GAP
                    const leafGroupWidth = leafEntity!.width
                    const totalWidth = nonLeafGroupWidth + HORIZONTAL_GAP + leafGroupWidth

                    // Option A: non-leaf group on left, leaf group on right
                    const nonLeafCenterLeft = centerX - totalWidth / 2 + nonLeafGroupWidth / 2
                    const leafCenterRight = centerX + totalWidth / 2 - leafGroupWidth / 2
                    const widerIsNonLeaf = nonLeafGroupWidth >= leafGroupWidth
                    const optionADistance = Math.abs((widerIsNonLeaf ? nonLeafCenterLeft : leafCenterRight) - 0)

                    // Option B: leaf group on left, non-leaf group on right
                    const leafCenterLeft = centerX - totalWidth / 2 + leafGroupWidth / 2
                    const nonLeafCenterRight = centerX + totalWidth / 2 - nonLeafGroupWidth / 2
                    const optionBDistance = Math.abs((widerIsNonLeaf ? nonLeafCenterRight : leafCenterLeft) - 0)

                    // Preserve baseline group side ordering:
                    // if the earliest non-leaf sibling comes before the earliest leaf sibling in the baseline,
                    // keep the non-leaf group on the left; otherwise swap.
                    let placeNonLeafOnLeft = optionADistance >= optionBDistance
                    if (baselineOrderForThisParent) {
                        const minNonLeafIdx = nonLeafChildren.reduce(
                            (min, c) => Math.min(min, baselineOrderForThisParent.get(c.profile.idStr) ?? Infinity),
                            Infinity,
                        )
                        const minLeafIdx = leafChildren.reduce(
                            (min, c) => Math.min(min, baselineOrderForThisParent.get(c.profile.idStr) ?? Infinity),
                            Infinity,
                        )
                        if (minNonLeafIdx !== Infinity || minLeafIdx !== Infinity) {
                            placeNonLeafOnLeft = minNonLeafIdx <= minLeafIdx
                        }
                    }

                    const nonLeafGroupCenter = placeNonLeafOnLeft ? nonLeafCenterLeft : nonLeafCenterRight
                    const leafGroupCenter = placeNonLeafOnLeft ? leafCenterRight : leafCenterLeft

                    // Children always start one row below the parent card.
                    placeEntities(
                        nonLeafEntities,
                        nonLeafGroupWidth,
                        nonLeafGroupCenter,
                        topRowIndex + 1,
                        node.profile.idStr,
                    )
                    placeLeafSpecial(leafEntity!.leafNodes, leafGroupCenter, topRowIndex + 1, leafEntity!.cols)

                    // Compact the two groups toward each other, then re-center under the parent.
                    const nonLeafGroupNodes = nonLeafEntities.flatMap((e) =>
                        e.kind === 'node' ? collectSubtreeNodes(e.node) : [],
                    )
                    const leafGroupNodes = [...leafEntity!.leafNodes]
                    const nonLeafIsLeft = nonLeafGroupCenter <= leafGroupCenter
                    const leftNodes = nonLeafIsLeft ? nonLeafGroupNodes : leafGroupNodes
                    const rightNodes = nonLeafIsLeft ? leafGroupNodes : nonLeafGroupNodes
                    const inwardShift = maxSafeLeftShift(rightNodes, leftNodes)
                    if (inwardShift > 1e-6) shiftNodes(rightNodes, -inwardShift)

                    const immediateChildren = [...nonLeafChildren, ...leafEntity!.leafNodes]
                    if (immediateChildren.length > 0) {
                        const immediateLeft = Math.min(...immediateChildren.map((c) => c.x))
                        const immediateRight = Math.max(...immediateChildren.map((c) => c.x + PROFILE_WIDTH))
                        const currentChildrenCenter = (immediateLeft + immediateRight) / 2
                        const recenterDelta = centerX - currentChildrenCenter
                        if (Math.abs(recenterDelta) > 1e-6) {
                            shiftNodes(nonLeafGroupNodes, recenterDelta)
                            shiftNodes(leafGroupNodes, recenterDelta)
                        }
                    }
                } else {
                    const entities: PlacementEntity[] = [...nonLeafEntities]
                    if (leafEntity) entities.push(leafEntity)

                    // Children always start one row below the parent card.
                    const totalSubtreeWidth =
                        entities.reduce((sum, e) => sum + e.width, 0) +
                        Math.max(0, entities.length - 1) * HORIZONTAL_GAP
                    placeEntities(entities, totalSubtreeWidth, centerX, topRowIndex + 1, node.profile.idStr)
                }

                // Parent anchor:
                // - mixed (leaf + non-leaf): center on the first horizontal bar center
                //   (midpoint between non-leaf group center and leaf group center)
                // - otherwise: center on immediate children span.
                const immediateChildren = node.children
                if (immediateChildren.length > 0) {
                    const immediateNonLeaf = immediateChildren.filter((c) => c.hasStructuralChildren)
                    const immediateLeaf = immediateChildren.filter((c) => !c.hasStructuralChildren)
                    if (immediateNonLeaf.length > 0 && immediateLeaf.length > 0) {
                        const nonLeafCenter =
                            immediateNonLeaf.reduce((sum, c) => sum + c.x + PROFILE_WIDTH / 2, 0) /
                            immediateNonLeaf.length
                        const leafCenter =
                            immediateLeaf.reduce((sum, c) => sum + c.x + PROFILE_WIDTH / 2, 0) / immediateLeaf.length
                        const firstBarCenter = (nonLeafCenter + leafCenter) / 2
                        node.x = firstBarCenter - PROFILE_WIDTH / 2
                    } else {
                        const immediateLeft = Math.min(...immediateChildren.map((c) => c.x))
                        const immediateRight = Math.max(...immediateChildren.map((c) => c.x + PROFILE_WIDTH))
                        node.x = (immediateLeft + immediateRight) / 2 - PROFILE_WIDTH / 2
                    }
                }
            }

            // Virtual root: its "children" are the actual top-level profiles (rendered on row 0).
            const rootNodes = topLevelProfiles
                .filter((p) => visibleIdSet.has(p.idStr))
                .map((p) => nodeMap.get(p.idStr)!)
                .filter(Boolean)

            // Ensure subtree dims exist for all non-leaf nodes.
            for (const root of rootNodes) {
                computeSubtreeDims(root)
            }
            const rawVirtualNonLeafChildren = rootNodes.filter((n) => n.hasStructuralChildren)
            const rawVirtualLeafChildren = rootNodes.filter((n) => !n.hasStructuralChildren)
            const promotedVirtualLeaf = rawVirtualLeafChildren.length === 1 ? rawVirtualLeafChildren[0] : null

            // Apply the same "single leaf -> non-leaf group" rule at virtual root.
            const virtualNonLeafChildren = promotedVirtualLeaf
                ? [...rawVirtualNonLeafChildren, promotedVirtualLeaf]
                : rawVirtualNonLeafChildren
            const virtualLeafChildren = promotedVirtualLeaf ? [] : rawVirtualLeafChildren

            const virtualNonLeafEntities: PlacementEntity[] = []
            for (const child of virtualNonLeafChildren) {
                const cd = subtreeDimsById.get(child.profile.idStr)
                if (!cd) continue
                virtualNonLeafEntities.push({
                    kind: 'node',
                    node: child,
                    width: cd.width,
                    heightRows: cd.heightRows,
                    layoutWidth: PROFILE_WIDTH,
                })
            }

            let virtualLeafEntity: PlacementEntity | null = null
            if (virtualLeafChildren.length > 0) {
                const leafSpecial = getLeafSpecialDims(virtualLeafChildren.length)
                virtualLeafEntity = {
                    kind: 'special',
                    leafNodes: virtualLeafChildren,
                    width: leafSpecial.width,
                    heightRows: leafSpecial.heightRows,
                    cols: leafSpecial.cols,
                    layoutWidth: leafSpecial.width,
                }
            }

            const virtualHasMixedGroups = virtualNonLeafEntities.length > 0 && virtualLeafEntity !== null

            if (virtualHasMixedGroups) {
                const nonLeafGroupWidth =
                    virtualNonLeafEntities.reduce((sum, e) => sum + e.width, 0) +
                    Math.max(0, virtualNonLeafEntities.length - 1) * HORIZONTAL_GAP
                const leafGroupWidth = virtualLeafEntity!.width
                const totalWidth = nonLeafGroupWidth + HORIZONTAL_GAP + leafGroupWidth

                const nonLeafCenterLeft = -totalWidth / 2 + nonLeafGroupWidth / 2
                const leafCenterRight = totalWidth / 2 - leafGroupWidth / 2
                const widerIsNonLeaf = nonLeafGroupWidth >= leafGroupWidth
                const optionADistance = Math.abs(widerIsNonLeaf ? nonLeafCenterLeft : leafCenterRight)

                const leafCenterLeft = -totalWidth / 2 + leafGroupWidth / 2
                const nonLeafCenterRight = totalWidth / 2 - nonLeafGroupWidth / 2
                const optionBDistance = Math.abs(widerIsNonLeaf ? nonLeafCenterRight : leafCenterLeft)

                const placeNonLeafOnLeft = optionADistance >= optionBDistance
                const nonLeafGroupCenter = placeNonLeafOnLeft ? nonLeafCenterLeft : nonLeafCenterRight
                const leafGroupCenter = placeNonLeafOnLeft ? leafCenterRight : leafCenterLeft

                placeEntities(virtualNonLeafEntities, nonLeafGroupWidth, nonLeafGroupCenter, 0, VIRTUAL_ROOT_KEY)
                placeLeafSpecial(virtualLeafEntity!.leafNodes, leafGroupCenter, 0, virtualLeafEntity!.cols)
            } else {
                const virtualEntities: PlacementEntity[] = [...virtualNonLeafEntities]
                if (virtualLeafEntity) virtualEntities.push(virtualLeafEntity)

                const virtualTotalWidth =
                    virtualEntities.reduce((sum, e) => sum + e.width, 0) +
                    Math.max(0, virtualEntities.length - 1) * HORIZONTAL_GAP

                // Place virtual root entities centered around X=0, starting on row 0.
                placeEntities(virtualEntities, virtualTotalWidth, 0, 0, VIRTUAL_ROOT_KEY)
            }

            // Compute subtree bounds (based on final compacted layout) so we can center parents
            // over the visual span of their full subtrees, not just card widths.
            type Bounds = {minX: number; maxX: number}
            const subtreeBoundsById = new Map<string, Bounds>()
            const computeSubtreeBounds = (node: TreeNode): Bounds => {
                let minX = node.x
                let maxX = node.x + PROFILE_WIDTH
                for (const child of node.children) {
                    const childBounds = computeSubtreeBounds(child)
                    minX = Math.min(minX, childBounds.minX)
                    maxX = Math.max(maxX, childBounds.maxX)
                }
                const bounds = {minX, maxX}
                subtreeBoundsById.set(node.profile.idStr, bounds)
                return bounds
            }
            for (const root of rootNodes) {
                computeSubtreeBounds(root)
            }

            // Final parent centering pass AFTER all compaction:
            // Ensure each parent is horizontally centered over the span of its children's subtrees.
            for (const node of nodeMap.values()) {
                if (!node.children.length) continue
                let subtreeLeft = Infinity
                let subtreeRight = -Infinity
                for (const child of node.children) {
                    const bounds = subtreeBoundsById.get(child.profile.idStr)
                    if (!bounds) continue
                    subtreeLeft = Math.min(subtreeLeft, bounds.minX)
                    subtreeRight = Math.max(subtreeRight, bounds.maxX)
                }
                if (!isFinite(subtreeLeft) || !isFinite(subtreeRight)) continue
                node.x = (subtreeLeft + subtreeRight) / 2 - PROFILE_WIDTH / 2
            }

            const allNodes: TreeNode[] = []

            // Collect nodes and validate positions.
            // Origin normalization must use *all* nodes (including hidden) to avoid relocation on collapse.
            for (const node of nodeMap.values()) {
                allNodes.push(node)

                // Ensure all nodes have valid positions.
                if (isNaN(node.x) || isNaN(node.y)) {
                    console.warn('Invalid node position:', node.profile.idStr, node.x, node.y)
                    continue
                }
            }

            // Update baseline ordering snapshot and frozen layout snapshot when fully expanded,
            // but only on the "real" layout pass.
            if (captureSnapshots && collapsedProfiles.size === 0) {
                const next = new Map<string, Map<string, number>>()
                for (const parent of nodeMap.values()) {
                    const orderedChildren = [...parent.children].sort((a, b) => a.x - b.x)
                    const idxMap = new Map<string, number>()
                    orderedChildren.forEach((ch, idx) => idxMap.set(ch.profile.idStr, idx))
                    next.set(parent.profile.idStr, idxMap)
                }

                const orderedRoots = [...rootNodes].sort((a, b) => a.x - b.x)
                const rootIdxMap = new Map<string, number>()
                orderedRoots.forEach((n, idx) => rootIdxMap.set(n.profile.idStr, idx))
                next.set(VIRTUAL_ROOT_KEY, rootIdxMap)

                baselineChildOrderIndexByParentRef.current = next
            }

            // Reset origin: shift all coordinates so top-left of tree is at (0, 0)
            if (allNodes.length > 0) {
                // Find the leftmost X and topmost Y
                let leftmostX = Infinity
                let topmostY = Infinity

                for (const node of allNodes) {
                    leftmostX = Math.min(leftmostX, node.x)
                    topmostY = Math.min(topmostY, node.y)
                }

                // Shift all nodes so top-left is at (0, 0)
                if (leftmostX !== Infinity && topmostY !== Infinity) {
                    const offsetX = -leftmostX
                    const offsetY = -topmostY

                    for (const node of allNodes) {
                        node.x += offsetX
                        node.y += offsetY
                    }
                }
            }

            if (captureSnapshots && collapsedProfiles.size === 0) {
                // Cache a full layout snapshot (normalized to origin) so subsequent collapses freeze coordinates.
                const nodesById = new Map<string, {x: number; y: number; hasStructuralChildren: boolean}>()
                const childrenById = new Map<string, string[]>()
                const rootsById: string[] = []

                for (const n of nodeMap.values()) {
                    nodesById.set(n.profile.idStr, {
                        x: n.x,
                        y: n.y,
                        hasStructuralChildren: n.hasStructuralChildren,
                    })
                    childrenById.set(
                        n.profile.idStr,
                        n.children.map((c) => c.profile.idStr),
                    )
                }

                for (const n of nodeMap.values()) {
                    if (!n.parent) rootsById.push(n.profile.idStr)
                }

                fullyExpandedLayoutSnapshotRef.current = {
                    signature: visibleSignature,
                    nodesById,
                    childrenById,
                    rootsById,
                }
            }

            // Compute approximate chart width/height in pixels for area comparison.
            let maxX = 0
            let maxY = 0
            for (const node of allNodes) {
                maxX = Math.max(maxX, node.x + PROFILE_WIDTH)
                maxY = Math.max(maxY, node.y)
            }
            const width = Math.max(maxX, 0)
            const height = Math.max((maxY + 1) * DEFAULT_ROW_HEIGHT_PX, 0)

            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.log('[HorizontalLayout] layout result', {
                    leafMaxCols,
                    captureSnapshots,
                    width,
                    height,
                    nodeCount: allNodes.length,
                    nodes: Array.from(allNodes).map((n) => ({
                        id: n.profile.idStr,
                        x: n.x,
                        y: n.y,
                        hasStructuralChildren: n.hasStructuralChildren,
                        childIds: n.children.map((c) => c.profile.idStr),
                    })),
                })
            }

            return {nodes: allNodes, width, height}
        }

        // When there are no collapsed profiles and no frozen snapshot yet, try leaf widths 1..5 and
        // pick the configuration with the smallest approximate chart area.
        if (!fullyExpandedLayoutSnapshotRef.current && collapsedProfiles.size === 0) {
            let bestResult: LayoutResult | null = null
            let bestCols = 5

            for (let cols = 2; cols <= 5; cols++) {
                const result = buildLayoutForLeafCols(cols, false)
                const area = result.width * result.height
                if (!bestResult || area < bestResult.width * bestResult.height) {
                    bestResult = result
                    bestCols = cols
                }
            }

            const finalResult = buildLayoutForLeafCols(bestCols, true)
            return finalResult.nodes
        }

        // Fallback: use the original 5-column configuration (e.g. when snapshot is already present or
        // collapse state is active before we ever captured a snapshot).
        const {nodes} = buildLayoutForLeafCols(5, true)
        return nodes
    }, [topLevelProfiles, visibleProfiles, getProfileFromIdStr, collapsedProfiles])

    const scrollToTop = () => {
        orgChartContainer.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
    }

    const removeFade = () => {
        orgChartContainer.current?.classList.remove('faded-background')
    }

    const addFade = () => {
        orgChartContainer.current?.classList.add('faded-background')
    }

    const onProfileHover = (_idStr: string | null, _prevIdStr: string | null) => {
        // No-op for horizontal view - hover effects not needed
    }

    const toggleExpand = (profileIdStr: string) => {
        const isCurrentlyCollapsed = collapsedProfiles.has(profileIdStr)
        if (isCurrentlyCollapsed) {
            // Expand immediately.
            const existingTimer = collapseTimersRef.current.get(profileIdStr)
            if (existingTimer) {
                window.clearTimeout(existingTimer)
                collapseTimersRef.current.delete(profileIdStr)
            }
            setCollapsingProfiles((prev) => {
                const next = new Set(prev)
                next.delete(profileIdStr)
                return next
            })
            setCollapsedProfiles((prev) => {
                const next = new Set(prev)
                next.delete(profileIdStr)
                return next
            })
            return
        }

        // Collapse: start fade phase first, apply hidden state after 0.3s.
        setCollapsingProfiles((prev) => {
            const next = new Set(prev)
            next.add(profileIdStr)
            return next
        })

        const existingTimer = collapseTimersRef.current.get(profileIdStr)
        if (existingTimer) {
            window.clearTimeout(existingTimer)
            collapseTimersRef.current.delete(profileIdStr)
        }
        const timeoutId = window.setTimeout(() => {
            setCollapsedProfiles((prev) => {
                const next = new Set(prev)
                next.add(profileIdStr)
                return next
            })
            setCollapsingProfiles((prev) => {
                const next = new Set(prev)
                next.delete(profileIdStr)
                return next
            })
            collapseTimersRef.current.delete(profileIdStr)
        }, 300)
        collapseTimersRef.current.set(profileIdStr, timeoutId)
    }

    const isExpanded = (profileIdStr: string) => {
        // Default to expanded (all items expanded by default)
        return !collapsedProfiles.has(profileIdStr)
    }

    // Cleanup pending collapse timers on unmount.
    React.useEffect(() => {
        const timers = collapseTimersRef.current
        return () => {
            for (const timeoutId of timers.values()) window.clearTimeout(timeoutId)
            timers.clear()
        }
    }, [])

    if (error) return <Typography.Heading>{getGraphQLErrorMessage(error)}</Typography.Heading>
    if (!data || loading) return <PageLoading />

    return (
        <OpenPositionProvider>
            <FullOrgChartProvider
                onHover={onProfileHover}
                scrollToTop={scrollToTop}
                removeFade={removeFade}
                addFade={addFade}
                orgChartContainer={orgChartContainer}
            >
                <DisplayOptionsProvider organisation={data.organisation}>
                    <OuterContainer>
                        <HorizontalOrgChartContent
                            treeNodes={treeNodes}
                            getProfileFromIdStr={getProfileFromIdStr}
                            isExpanded={isExpanded}
                            toggleExpand={toggleExpand}
                            collapsingProfileIds={collapsingProfiles}
                            collapsedProfileIds={collapsedProfiles}
                        />
                    </OuterContainer>
                </DisplayOptionsProvider>
            </FullOrgChartProvider>
        </OpenPositionProvider>
    )
}
